import type { GridColDef } from '@mui/x-data-grid-premium';
import type { CustomFieldValue } from '@vy/custom-fields-types';
import { defined } from '@vy/null';
import type { CustomerMasterReport, IntegrationProviders } from '@vy/types';
import { useMemo } from 'react';
import { CUSTOMER_MASTER_CUSTOM_FIELD_PREFIXES } from '../customer-master-report-page-components/customer-master-report-page-table/customer-master-report-constants';
import {
  buildContactColumns,
  CUSTOMER_MASTER_REPORT_BUILTIN_COLUMNS,
} from '../customer-master-report-page-components/customer-master-report-page-table/CustomerMasterReportTableColumns';
import type { CustomerMasterRow } from '../customer-master-report-page-components/customer-master-report-page-table/customer-master-row-types';
import { isColumnVisibleForConnectedIntegrations } from '../customer-master-report-page-components/customer-master-report-page-table/integration-column-visibility';

/**
 * Builds the full column list for the Customer Master grid:
 * built-in customer + contract columns, followed by dynamic custom-field
 * columns and dynamic Contact 1..N columns at the end.
 *
 * - Custom-field columns are derived by walking the response and unioning every
 *   `customFieldValues[].fieldName` seen at the customer and contract level.
 * - Contact columns are sized to the largest `contacts.length` across all
 *   customers in the report — accounts with three contacts get nine extra
 *   columns (3 contacts × {Name, Title, Phone, Email} minus title sharing).
 *
 * Both kinds are placed after the built-in columns so the well-known fields
 * sit on the left and the variable-width groups push to the right.
 */
export function useGetCustomerMasterReportColumns(
  report: CustomerMasterReport,
  connectedProviders: ReadonlySet<IntegrationProviders>,
): GridColDef<CustomerMasterRow>[] {
  return useMemo(() => {
    const customerCustomFieldNames = collectCustomFieldNames(
      report.flatMap((customer) => customer.customFieldValues ?? []),
    );
    const contractCustomFieldNames = collectCustomFieldNames(
      report.flatMap((customer) =>
        customer.contracts.flatMap((contract) => contract.customFieldValues ?? []),
      ),
    );

    const customerCustomFieldColumns = customerCustomFieldNames.map((fieldName) =>
      buildCustomerCustomFieldColumn(fieldName),
    );
    const contractCustomFieldColumns = contractCustomFieldNames.map((fieldName) =>
      buildContractCustomFieldColumn(fieldName),
    );

    const maxContactsCount = report.reduce(
      (currentMax, customer) => Math.max(currentMax, customer.contacts?.length ?? 0),
      0,
    );
    const contactIndices = Array.from({ length: maxContactsCount }, (_unusedElement, contactIndex) => contactIndex);
    const contactColumns = contactIndices.flatMap(buildContactColumns);

    const visibleBuiltinColumns = CUSTOMER_MASTER_REPORT_BUILTIN_COLUMNS.filter((column) =>
      isColumnVisibleForConnectedIntegrations(column.field, connectedProviders),
    );

    return [
      ...visibleBuiltinColumns,
      ...customerCustomFieldColumns,
      ...contractCustomFieldColumns,
      ...contactColumns,
    ];
  }, [report, connectedProviders]);
}

function collectCustomFieldNames(customFieldValues: CustomFieldValue[]): string[] {
  const fieldNames = new Set<string>();
  for (const customField of customFieldValues) {
    if (defined(customField.fieldName) && customField.fieldName !== '') {
      fieldNames.add(customField.fieldName);
    }
  }
  return [...fieldNames].sort((leftName, rightName) =>
    leftName.localeCompare(rightName, undefined, { sensitivity: 'base' }),
  );
}

function buildCustomerCustomFieldColumn(fieldName: string): GridColDef<CustomerMasterRow> {
  const field = `${CUSTOMER_MASTER_CUSTOM_FIELD_PREFIXES.customer}${fieldName}`;
  return {
    field,
    headerName: field,
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    hideable: true,
    valueGetter: (_value, row) => {
      const value = row.customFieldValues?.find((customField) => customField.fieldName === fieldName)?.value;
      return value ?? null;
    },
  };
}

function buildContractCustomFieldColumn(fieldName: string): GridColDef<CustomerMasterRow> {
  const field = `${CUSTOMER_MASTER_CUSTOM_FIELD_PREFIXES.contract}${fieldName}`;
  return {
    field,
    headerName: field,
    headerClassName: 'text-sm',
    flex: 1,
    minWidth: 150,
    headerAlign: 'left',
    align: 'left',
    hideable: true,
    valueGetter: (_value, row) => {
      const value = row.contractCustomFieldValues?.find((customField) => customField.fieldName === fieldName)?.value;
      return value ?? null;
    },
  };
}
